import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import api, { createFormData } from '../../services/api';

const Settings = () => {
  const [settings, setSettings] = useState({
    privacy_hide_profile: false,
    privacy_hide_last_login: false,
    notifications_message: true,
    notifications_offer: true,
    notifications_favorite: true,
    notifications_view: true,
    notifications_promotion: true
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch user settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await api.get('/settings');
        if (response.data && response.data.data) {
          setSettings(response.data.data);
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
        setError('Failed to load settings. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Use the createFormData helper function
      const formData = createFormData(settings);

      // Send the request with the correct headers
      await api.patch('/settings', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess('Settings saved successfully!');
      console.log('Settings saved successfully');
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading settings...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <h2 className="fw-bold mb-4">Privacy & Security Settings</h2>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-4">
          {success}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col lg={6} className="mb-4">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-4">
                <h4 className="fw-bold mb-4">Privacy Settings</h4>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="privacy_hide_profile"
                    name="privacy_hide_profile"
                    label="Hide my profile from search results"
                    checked={settings.privacy_hide_profile}
                    onChange={handleChange}
                  />
                  <Form.Text className="text-muted">
                    Your profile will not appear in search results, but you can still be messaged by users you've interacted with.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="privacy_hide_last_login"
                    name="privacy_hide_last_login"
                    label="Hide my last login time"
                    checked={settings.privacy_hide_last_login}
                    onChange={handleChange}
                  />
                  <Form.Text className="text-muted">
                    Other users will not see when you were last online.
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6} className="mb-4">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-4">
                <h4 className="fw-bold mb-4">Email Notifications</h4>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="notifications_message"
                    name="notifications_message"
                    label="New messages"
                    checked={settings.notifications_message}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="notifications_offer"
                    name="notifications_offer"
                    label="Date offers"
                    checked={settings.notifications_offer}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="notifications_favorite"
                    name="notifications_favorite"
                    label="New favorites"
                    checked={settings.notifications_favorite}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="notifications_view"
                    name="notifications_view"
                    label="Profile views"
                    checked={settings.notifications_view}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="notifications_promotion"
                    name="notifications_promotion"
                    label="Promotions and updates"
                    checked={settings.notifications_promotion}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <div className="d-grid gap-2 col-md-6 mx-auto mt-4">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={saving}
          >
            {saving ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Saving...
              </>
            ) : 'Save Settings'}
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default Settings;
